import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Loader2, Receipt } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyInvoices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/subscriptions/me/list`, { withCredentials: true })
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-lg" data-testid="my-invoices-empty">
        <Receipt className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No invoices yet. Once you subscribe, your invoices and payment receipts will appear here.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="my-invoices-section">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Invoice No.</th>
              <th className="text-left py-2 px-3 font-medium">Plan</th>
              <th className="text-left py-2 px-3 font-medium">Date</th>
              <th className="text-right py-2 px-3 font-medium">Amount</th>
              <th className="text-left py-2 px-3 font-medium">Status</th>
              <th className="text-left py-2 px-3 font-medium">Valid Till</th>
              <th className="text-center py-2 px-3 font-medium">Download</th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-border" data-testid={`invoice-row-${s.id}`}>
                <td className="py-2 px-3 font-mono text-xs">{s.invoice_no}</td>
                <td className="py-2 px-3">{s.plan_name}{s.is_founding && <span className="ml-1 text-xs text-gold font-bold">★</span>}</td>
                <td className="py-2 px-3 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                <td className="py-2 px-3 text-right">₹{(s.total_amount || 0).toLocaleString('en-IN')}</td>
                <td className="py-2 px-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.payment_status === 'paid' ? 'bg-green-100 text-green-800' : s.payment_status === 'complimentary' ? 'bg-gold/30 text-navy' : 'bg-navy/10 text-navy'}`}>
                    {s.payment_status}
                  </span>
                </td>
                <td className="py-2 px-3 text-xs">{s.end_date ? new Date(s.end_date).toLocaleDateString() : '-'}</td>
                <td className="py-2 px-3 text-center">
                  <a href={`${API}/payments/invoice/${s.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-navy hover:text-orange text-xs" data-testid={`invoice-download-${s.id}`}>
                    <Download className="h-3 w-3 mr-1" /> PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-border">
        {items.map(s => (
          <div key={s.id} className="p-4" data-testid={`invoice-card-${s.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-mono text-xs text-muted-foreground">{s.invoice_no}</div>
                <div className="font-bold text-navy mt-1">{s.plan_name} {s.is_founding && <span className="text-gold">★</span>}</div>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.payment_status === 'paid' ? 'bg-green-100 text-green-800' : s.payment_status === 'complimentary' ? 'bg-gold/30 text-navy' : 'bg-navy/10 text-navy'}`}>
                {s.payment_status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
              <div><span className="font-medium">Date:</span> {s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</div>
              <div><span className="font-medium">Amount:</span> ₹{(s.total_amount || 0).toLocaleString('en-IN')}</div>
              <div className="col-span-2"><span className="font-medium">Valid till:</span> {s.end_date ? new Date(s.end_date).toLocaleDateString() : '-'}</div>
            </div>
            <a href={`${API}/payments/invoice/${s.id}`} target="_blank" rel="noopener noreferrer" className="w-full px-3 py-2 rounded bg-navy text-white text-xs font-semibold inline-flex items-center justify-center" data-testid={`invoice-download-mobile-${s.id}`}>
              <Download className="h-3 w-3 mr-2" /> Download Invoice & Receipt
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyInvoices;
